<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ $t('_accessLevelConfig') }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- A — ขั้นบันได (cus.AccessLevel, #914) -->
      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <v-col cols="12" md="8" class="d-flex align-center">
              <v-icon color="blue-grey-darken-2" class="mr-5" size="28">mdi-stairs</v-icon>
              <div>
                <h3 class="text-h6 mb-1">{{ $t('accessLevelLadderTitle') }}</h3>
                <p class="text-caption text-grey-darken-1 mb-0 description">{{ $t('accessLevelLadderHint') }}</p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="d-flex justify-md-end mt-3 mt-md-0">
              <v-btn
                v-if="canAddLevel"
                color="primary"
                variant="flat"
                class="text-none"
                prepend-icon="mdi-plus"
                :disabled="!ladderWritable || ladderBusy || orderDirty"
                @click="openAddLevel"
              >
                {{ $t('accessLevelLadderAdd') }}
                <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderAddTooltip') }}</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="px-4">
          <v-progress-linear v-if="sectionsLoading" indeterminate color="primary" class="mb-4" />
          <v-alert v-if="ladderError" type="error" variant="tonal" density="comfortable" class="mb-4">
            {{ $t('accessLevelLadderLoadError', { message: ladderError }) }}
          </v-alert>
          <v-alert v-else-if="ladderLoaded && !ladderFromDatabase" type="info" variant="tonal" density="comfortable" class="mb-4">
            {{ $t('accessLevelLadderBuiltInBanner') }}
            <div v-if="ladderWarning" class="text-caption mt-1">{{ ladderWarning }}</div>
          </v-alert>
          <v-alert v-if="usageError" type="warning" variant="tonal" density="compact" class="mb-4">
            {{ $t('accessLevelLadderUsageLoadError', { message: usageError }) }}
          </v-alert>

          <access-level-ladder-table
            :levels="draftLevels"
            :moved-codes="movedCodes"
            :required-codes="requiredCodes"
            :usage-counts="usageCounts"
            :can-reorder="canEditLevel && ladderWritable"
            :reorder-disabled="ladderBusy"
            :can-edit="canEditLevel && ladderWritable"
            :edit-disabled="ladderBusy"
            :can-delete="canDeleteLevel && ladderWritable"
            :delete-disabled="ladderBusy || orderDirty"
            :loading="sectionsLoading && !ladderLoaded"
            @move="moveLevel"
            @edit="openEditLevel"
            @remove="confirmDeleteLevel"
            @show-usage="openUsage"
          />
          <div v-if="usage" class="text-caption text-grey-darken-1 mt-2">
            {{ $t('accessLevelLadderScanned', { flows: usage.scannedFlows, nodes: usage.scannedNodes }) }}
            <span v-if="usage.warnings.length" :title="usage.warnings.join('\n')">
              · {{ $t('accessLevelLadderScanWarnings', { count: usage.warnings.length }) }}
            </span>
          </div>

          <v-alert v-if="orderDirty" type="warning" variant="tonal" density="comfortable" class="mt-4">
            <div class="d-flex align-center flex-wrap ga-2">
              <div class="flex-grow-1">
                <strong>{{ $t('accessLevelLadderUnsavedOrder') }}</strong>
                ·
                {{
                  affectedIntentCount === null
                    ? $t('accessLevelLadderAffectedUnknown')
                    : $t('accessLevelLadderAffectedIntents', { count: affectedIntentCount })
                }}
                <div class="text-caption">{{ $t('accessLevelLadderDraftLocksHint') }}</div>
              </div>
              <v-btn variant="text" class="text-none" :disabled="ladderBusy" @click="cancelOrder">
                {{ $t('accessLevelLadderCancelOrder') }}
                <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderCancelOrderTooltip') }}</v-tooltip>
              </v-btn>
              <v-btn
                v-if="canEditLevel"
                color="primary"
                variant="flat"
                class="text-none"
                prepend-icon="mdi-content-save"
                :loading="ladderSaving"
                :disabled="!ladderWritable || ladderBusy"
                @click="saveOrder"
              >
                {{ $t('accessLevelLadderSaveOrder') }}
                <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderSaveOrderTooltip') }}</v-tooltip>
              </v-btn>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- C — Preview (อ่านอย่างเดียว, คำนวณจากลำดับร่าง) -->
      <v-card class="card2">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <v-col cols="12" class="d-flex align-center">
              <v-icon color="blue-grey-darken-2" class="mr-5" size="28">mdi-table-check</v-icon>
              <div class="flex-grow-1">
                <h3 class="text-h6 mb-1">
                  {{ $t('accessLevelPreviewTitle') }}
                  <v-chip v-if="orderDirty" size="small" label variant="tonal" color="warning" class="ml-2">
                    {{ $t('accessLevelPreviewDraft') }}
                  </v-chip>
                </h3>
                <p class="text-caption text-grey-darken-1 mb-0 description">{{ $t('accessLevelPreviewHint') }}</p>
              </div>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="px-4">
          <access-level-preview-matrix :levels="draftLevels" />
          <div class="text-caption text-grey-darken-1 mt-2">{{ $t('accessLevelPreviewCaption') }}</div>
          <div class="text-caption text-grey-darken-1 mt-1">{{ $t('accessLevelPreviewGenieNote') }}</div>
        </v-card-text>
      </v-card>

      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <v-col cols="12" class="d-flex align-center">
              <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-message-lock-outline</v-icon>
              <div>
                <h3 class="text-h6 mb-1">ข้อความเมื่อสิทธิ์ไม่พอ</h3>
                <p class="text-caption text-grey-darken-1 mb-0 description">
                  ส่งต่อท้าย <code>:ACCESS_LEVEL_REQUIRED:</code> ตามภาษาของแอป · ภาษาที่เว้นว่างจะใช้ข้อความภาษาไทยแทน ·
                  ว่างทั้งหมด = ส่ง marker อย่างเดียว
                </p>
              </div>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="px-4">
          <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />
          <v-alert v-if="loadError" type="error" variant="tonal" density="comfortable" class="mb-4">
            {{ loadError }}
          </v-alert>
          <v-form ref="form" v-model="formValid" @submit.prevent="saveConfig">
            <v-textarea
              v-for="lang in languages"
              :key="lang.key"
              v-model="deniedMessage[lang.key]"
              :label="lang.label"
              :rules="[rules.maxLength, rules.noPipe]"
              :counter="maxLength"
              :disabled="isViewOnly || loading"
              variant="outlined"
              rows="3"
              auto-grow
              class="mb-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn
            color="primary"
            class="btn-layout text-none"
            :loading="saving"
            :disabled="isViewOnly || loading || !formValid"
            @click="saveConfig"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- เพิ่ม / แก้ชื่อระดับ -->
    <access-level-tier-dialog
      v-model="levelDialog.show"
      :mode="levelDialog.mode"
      :level="levelDialog.level"
      :existing-codes="savedCodes"
      :insert-after-options="insertAfterOptions"
      :default-insert-after="defaultInsertAfter"
      :saving="ladderSaving"
      :error="levelDialog.error"
      @submit="submitLevelDialog"
    />

    <!-- รายการ intent ที่ตั้งระดับ -->
    <v-dialog v-model="usageDialog.show" max-width="760px">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
          <span>{{ $t('accessLevelLadderUsageTitle', { code: usageDialog.code }) }}</span>
        </v-card-title>
        <v-card-text class="pt-4 dialog-scroll">
          <access-level-usage-list :intents="usageDialogIntents" :empty-text="$t('accessLevelLadderUsageEmpty')" />
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="usageDialog.show = false">
            {{ $t('accessLevelLadderClose') }}
            <v-tooltip activator="parent" location="top">{{ $t('accessLevelLadderClose') }}</v-tooltip>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ยืนยันลบ / ลบไม่ได้ (409) -->
    <access-level-delete-dialog
      v-model="deleteDialog.show"
      :code="deleteLevelCode"
      :conflict="deleteDialog.conflict"
      :error="deleteDialog.error"
      :saving="ladderSaving"
      :can-delete="canDeleteLevel"
      @confirm="deleteLevel"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" location="top">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import { accessLevelApi } from '@/services/accessLevelApi';
import { refreshAccessLevels } from '@/utils/accessLevelStore';
import { ACCESS_LEVELS, getAccessLevelLadder } from '@/utils/accessLevel';
import AccessLevelLadderTable from '@/components/AccessLevel/AccessLevelLadderTable.vue';
import AccessLevelPreviewMatrix from '@/components/AccessLevel/AccessLevelPreviewMatrix.vue';
import AccessLevelUsageList from '@/components/AccessLevel/AccessLevelUsageList.vue';
import AccessLevelTierDialog from '@/components/AccessLevel/AccessLevelTierDialog.vue';
import AccessLevelDeleteDialog from '@/components/AccessLevel/AccessLevelDeleteDialog.vue';

const MAX_LENGTH = 1000;

// ── ขั้นบันได (#914) — สัญญา API: `api/v1/AccessLevel*` (AccessLevelTierController)
/**
 * role ที่ `APIAuthen` เปิดให้เขียน (create / {id} / reorder = ["0","1"]) — role อื่นยิงแล้ว
 * CookieValidate sign out ⇒ ปุ่มเขียนต้องไม่โผล่ให้ role 2 แม้ `$pagePermission` จะให้สิทธิ์แก้หน้า
 */
const LADDER_WRITE_ROLES = ['0', '1'];
/** `GET api/v1/AccessLevel/usage` เปิดถึง role 0,1,2 */
const USAGE_READ_ROLES = ['0', '1', '2'];

/** trim + ตัวพิมพ์ใหญ่ (เทียบ code แบบเดียวกับ `AccessLevelPolicy.Normalize`) */
function upperText(value) {
  return value == null ? '' : String(value).trim().toUpperCase();
}

function textOf(value) {
  return value == null ? '' : String(value);
}

/** อ่าน field แบบ camelCase ก่อน แล้ว PascalCase */
function pick(source, camel, pascal) {
  if (!source || typeof source !== 'object') return undefined;
  return source[camel] !== undefined ? source[camel] : source[pascal];
}

/** `AccessLevelTierDto` → แถวของตาราง A */
function toLevelRow(row) {
  const code = upperText(pick(row, 'code', 'Code'));
  return {
    id: Number(pick(row, 'id', 'Id')) || 0,
    code,
    nameTh: textOf(pick(row, 'nameTh', 'NameTh')),
    nameEn: textOf(pick(row, 'nameEn', 'NameEn')),
    description: textOf(pick(row, 'description', 'Description')),
    sortOrder: Number(pick(row, 'sortOrder', 'SortOrder')) || 0,
    isSystem: Boolean(pick(row, 'isSystem', 'IsSystem')) || code === 'T0' || code === 'T3',
  };
}

/** สมาชิกชุดเดียวกัน (ไม่สนลำดับ) */
function sameMembers(a, b) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((code) => set.has(code));
}

/**
 * ระดับจริงของ intent ตามเส้นทาง (A7) = code ที่ index สูงสุดใน `codes` ของทุกชั้นราก → intent
 * · null/ว่าง ⇒ ล่างสุด (T0) · ไม่รู้จัก ⇒ บนสุด (fail-closed เหมือน `NormalizeRequired`)
 */
function effectiveCodeOf(chainLevels, codes) {
  if (!codes.length) return '';
  let best = 0;
  (Array.isArray(chainLevels) ? chainLevels : []).forEach((raw) => {
    const code = upperText(raw);
    if (!code) return;
    const index = codes.indexOf(code);
    const rank = index < 0 ? codes.length - 1 : index;
    if (rank > best) best = rank;
  });
  return codes[best];
}

/**
 * ระดับผู้ใช้ที่ผ่าน intent ตามลำดับ `codes` = ทุก code ที่ rank ≥ rank ของระดับจริงตามเส้นทาง
 * (กติกาเดียวกับ `AccessLevelPolicy.IsAllowed`)
 */
function passingUsersOf(chainLevels, codes) {
  const index = codes.indexOf(effectiveCodeOf(chainLevels, codes));
  return index < 0 ? [] : codes.slice(index);
}

function errorTextOf(err, fallback) {
  return (err && err.message) || fallback;
}

export default {
  name: 'AccessLevelConfig',
  components: {
    AccessLevelLadderTable,
    AccessLevelPreviewMatrix,
    AccessLevelUsageList,
    AccessLevelTierDialog,
    AccessLevelDeleteDialog,
  },
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  data() {
    const todayDate = new Date();
    const dd = String(todayDate.getDate()).padStart(2, '0');
    const mm = String(todayDate.getMonth() + 1).padStart(2, '0');
    const yyyy = todayDate.getFullYear();

    return {
      today: `${dd}/${mm}/${yyyy}`,
      loading: false,
      saving: false,
      formValid: true,
      loadError: '',
      maxLength: MAX_LENGTH,
      languages: [
        { key: 'th', label: 'ไทย' },
        { key: 'en', label: 'English' },
        { key: 'zh', label: '中文' },
      ],
      deniedMessage: { th: '', en: '', zh: '' },
      snackbar: { show: false, text: '', color: 'success' },
      rules: {
        maxLength: (v) => !v || v.length <= MAX_LENGTH || `ยาวได้ไม่เกิน ${MAX_LENGTH} ตัวอักษร`,
        noPipe: (v) => !v || !v.includes('|') || "ห้ามมีอักษร '|'",
      },
      // ── ขั้นบันได / preview (#914)
      sectionsLoading: false,
      ladderSaving: false,
      ladderLoaded: false,
      ladderLevels: [],
      ladderFromDatabase: false,
      ladderWarning: '',
      ladderError: '',
      draftCodes: [],
      usage: null,
      usageError: '',
      levelDialog: { show: false, mode: 'add', level: null, error: '' },
      usageDialog: { show: false, code: '' },
      deleteDialog: { show: false, level: null, conflict: null, error: '' },
    };
  },
  computed: {
    isViewOnly() {
      return !!(this.$pagePermission && this.$pagePermission.isViewOnly('AccessLevelConfig'));
    },
    roleId() {
      return textOf(Cookies.get('roleId')).trim();
    },
    canWriteLadderRole() {
      return LADDER_WRITE_ROLES.includes(this.roleId);
    },
    canReadUsage() {
      return USAGE_READ_ROLES.includes(this.roleId);
    },
    canAddLevel() {
      return (
        !this.isViewOnly &&
        this.canWriteLadderRole &&
        !!(this.$pagePermission && this.$pagePermission.canAdd('AccessLevelConfig'))
      );
    },
    canEditLevel() {
      return (
        !this.isViewOnly &&
        this.canWriteLadderRole &&
        !!(this.$pagePermission && this.$pagePermission.canEdit('AccessLevelConfig'))
      );
    },
    canDeleteLevel() {
      return (
        !this.isViewOnly &&
        this.canWriteLadderRole &&
        !!(this.$pagePermission && this.$pagePermission.canDelete('AccessLevelConfig'))
      );
    },
    /** A12: ตารางว่าง/ยังไม่รัน SQL/โหลดพลาด ⇒ ค่าในตัว อ่านอย่างเดียว (ปิดปุ่มแก้ของ A) */
    ladderWritable() {
      return this.ladderLoaded && this.ladderFromDatabase && !this.ladderError;
    },
    ladderBusy() {
      return this.sectionsLoading || this.ladderSaving;
    },
    /** code ที่โค้ดอ้างเป็น const (T0–T3 = `ACCESS_LEVELS`) ⇒ ตาราง A ไม่มีปุ่มลบ (server ตอบ 400 อยู่แล้ว) */
    requiredCodes() {
      return ACCESS_LEVELS;
    },
    savedCodes() {
      return this.ladderLevels.map((level) => level.code);
    },
    levelByCode() {
      const map = {};
      this.ladderLevels.forEach((level) => {
        map[level.code] = level;
      });
      return map;
    },
    /** ระดับตามลำดับร่าง (ตาราง A และ preview C ใช้ชุดนี้) */
    draftLevels() {
      return this.draftCodes.map((code) => this.levelByCode[code]).filter(Boolean);
    },
    orderDirty() {
      return this.draftCodes.join('|') !== this.savedCodes.join('|');
    },
    movedCodes() {
      return this.draftCodes.filter((code, index) => this.savedCodes[index] !== code);
    },
    insertAfterOptions() {
      return this.ladderLevels.slice(0, -1).map((level) => ({
        title: level.nameTh || level.nameEn ? `${level.code} — ${level.nameTh || level.nameEn}` : level.code,
        value: level.code,
      }));
    },
    usageIntents() {
      return this.usage ? this.usage.intents : [];
    },
    /** A7: code (ตัวพิมพ์ใหญ่) → จำนวน intent ที่ตั้งระดับนั้นเอง (`rawLevel`) · null = นับไม่ได้ */
    usageCounts() {
      if (!this.usage) return null;
      const counts = {};
      const byCode = this.usage.countByCode;
      if (byCode && typeof byCode === 'object' && Object.keys(byCode).length) {
        Object.keys(byCode).forEach((code) => {
          const key = upperText(code);
          counts[key] = (counts[key] || 0) + (Number(byCode[code]) || 0);
        });
        return counts;
      }
      this.usage.intents.forEach((intent) => {
        const key = upperText(intent.rawLevel);
        if (key) counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    },
    /**
     * intent ที่ได้รับผลกระทบจากลำดับร่าง = intent ที่**ชุดระดับผู้ใช้ที่ผ่าน**เปลี่ยนไปเมื่อเทียบกับลำดับที่บันทึก
     * (เช่น intent `[T2.1]` เมื่อสลับ T2 ↔ T2.1 ⇒ ผู้ใช้ T2 เปลี่ยนจากถูกตีกลับเป็นผ่าน) · intent T0/T3 ไม่มีทางถูกนับ
     * (T0 ผ่านทุกคน, T3 ล็อกบนสุด) · null = นับไม่ได้ (โหลด usage ไม่สำเร็จ)
     */
    affectedIntentCount() {
      if (!this.usage) return null;
      if (!this.orderDirty) return 0;
      const saved = this.savedCodes;
      const draft = this.draftCodes;
      return this.usage.intents.filter(
        (intent) => !sameMembers(passingUsersOf(intent.chainLevels, saved), passingUsersOf(intent.chainLevels, draft))
      ).length;
    },
    usageDialogIntents() {
      const code = this.usageDialog.code;
      return this.usageIntents.filter((intent) => upperText(intent.rawLevel) === code);
    },
    /** "แทรกหลัง" เริ่มต้น = ระดับรองสุดท้าย (ระดับใหม่อยู่ใต้ T3 ทันที) */
    defaultInsertAfter() {
      const options = this.insertAfterOptions;
      return options.length ? options[options.length - 1].value : '';
    },
    deleteLevelCode() {
      return (this.deleteDialog.level && this.deleteDialog.level.code) || '';
    },
  },
  mounted() {
    this.loadConfig();
    this.loadLadderSections();
  },
  methods: {
    applyDeniedMessage(data) {
      const msg = (data && (data.deniedMessage || data.DeniedMessage)) || {};
      this.deniedMessage = {
        th: msg.th ?? msg.Th ?? '',
        en: msg.en ?? msg.En ?? '',
        zh: msg.zh ?? msg.Zh ?? '',
      };
    },
    errorMessageOf(errorResource, fallback) {
      const body = errorResource && errorResource.response && errorResource.response.data;
      return (body && (body.message || body.Message)) || fallback;
    },
    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },
    loadConfig() {
      this.loading = true;
      this.loadError = '';
      this.$call.GetAccessLevelConfig(
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.applyDeniedMessage(dataResponse.data);
          } else {
            this.loadError = dataResponse.message || 'โหลดการตั้งค่า Access Level ไม่สำเร็จ';
          }
          this.loading = false;
        },
        (errorResource) => {
          console.error('GetAccessLevelConfig error:', errorResource);
          const status = errorResource && errorResource.response && errorResource.response.status;
          this.loadError =
            status === 500
              ? 'โหลดการตั้งค่า Access Level ไม่สำเร็จ (ตาราง cus.AccessLevelConfig อาจยังไม่ถูกสร้าง)'
              : this.errorMessageOf(errorResource, 'โหลดการตั้งค่า Access Level ไม่สำเร็จ');
          this.loading = false;
        }
      );
    },
    async saveConfig() {
      if (this.isViewOnly) return;
      const validation = await this.$refs.form.validate();
      if (!validation.valid) return;

      this.saving = true;
      const payload = {
        deniedMessage: {
          th: this.deniedMessage.th || '',
          en: this.deniedMessage.en || '',
          zh: this.deniedMessage.zh || '',
        },
      };
      this.$call.SaveAccessLevelConfig(
        payload,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.applyDeniedMessage(dataResponse.data);
            this.showSnackbar('บันทึกเรียบร้อย', 'success');
          } else {
            this.showSnackbar(dataResponse.message || 'บันทึกไม่สำเร็จ', 'error');
          }
          this.saving = false;
        },
        (errorResource) => {
          console.error('SaveAccessLevelConfig error:', errorResource);
          const status = errorResource && errorResource.response && errorResource.response.status;
          const fallback =
            status === 500
              ? 'บันทึกไม่สำเร็จ (ตาราง cus.AccessLevelConfig อาจยังไม่ถูกสร้าง)'
              : 'บันทึกไม่สำเร็จ';
          this.showSnackbar(status === 400 ? this.errorMessageOf(errorResource, fallback) : fallback, 'error');
          this.saving = false;
        }
      );
    },

    // ── ขั้นบันได / preview (#914) ─────────────────────────────────────
    /** โหลดส่วน A/C พร้อมกัน (ขั้นบันได + usage) · ส่วนใดพลาดไม่บล็อกส่วนอื่น (usage พลาด ⇒ "นับไม่ได้") · ไม่ throw */
    async loadLadderSections() {
      this.sectionsLoading = true;
      try {
        const usageRequest = this.canReadUsage
          ? accessLevelApi.usage()
          : Promise.reject(new Error(this.$t('accessLevelLadderUsageNoPermission')));
        const [ladderResult, usageResult] = await Promise.allSettled([accessLevelApi.list(), usageRequest]);

        if (ladderResult.status !== 'fulfilled') {
          this.applyBuiltInLadder(errorTextOf(ladderResult.reason, this.$t('accessLevelLadderLoadFailed')));
        } else if (!this.applyLadder(ladderResult.value)) {
          this.applyBuiltInLadder(this.$t('accessLevelLadderLoadFailed'));
        }

        if (usageResult.status === 'fulfilled') {
          this.applyUsage(usageResult.value);
        } else {
          this.usage = null;
          this.usageError = errorTextOf(usageResult.reason, this.$t('accessLevelLadderLoadFailed'));
        }

        this.syncDrafts();
      } catch (err) {
        console.error('loadLadderSections error:', err);
      } finally {
        this.sectionsLoading = false;
      }
    },
    /** `AccessLevelLadderDto` → state · คืน false เมื่อรูปข้อมูลใช้ไม่ได้ */
    applyLadder(data) {
      const rows = pick(data, 'levels', 'Levels');
      if (!Array.isArray(rows) || rows.length === 0) return false;
      const levels = rows.map(toLevelRow).filter((level) => level.code);
      if (!levels.length) return false;
      levels.sort((a, b) => a.sortOrder - b.sortOrder);
      this.ladderLevels = levels;
      this.ladderFromDatabase = Boolean(pick(data, 'fromDatabase', 'FromDatabase'));
      this.ladderWarning = textOf(pick(data, 'warning', 'Warning'));
      this.ladderError = '';
      this.ladderLoaded = true;
      return true;
    },
    /** โหลดขั้นบันไดไม่ได้ ⇒ แสดง ladder ที่หน้าเว็บรู้อยู่ (ค่าในตัว T0–T3) แบบอ่านอย่างเดียว */
    applyBuiltInLadder(message) {
      const ladder = getAccessLevelLadder();
      this.ladderLevels = ladder.map((level, index) => ({
        ...toLevelRow(level),
        sortOrder: index + 1,
        isSystem: index === 0 || index === ladder.length - 1,
      }));
      this.ladderFromDatabase = false;
      this.ladderWarning = '';
      this.ladderError = message;
      this.ladderLoaded = true;
    },
    /** `AccessLevelUsageDto` → state (เติมค่าว่างให้ครบรูป) */
    applyUsage(data) {
      const intents = pick(data, 'intents', 'Intents');
      const warnings = pick(data, 'warnings', 'Warnings');
      const countByCode = pick(data, 'countByCode', 'CountByCode');
      this.usage = {
        intents: (Array.isArray(intents) ? intents : []).map((intent) => ({
          flowId: pick(intent, 'flowId', 'FlowId'),
          flowName: textOf(pick(intent, 'flowName', 'FlowName')),
          nodeId: pick(intent, 'nodeId', 'NodeId'),
          nodeType: textOf(pick(intent, 'nodeType', 'NodeType')),
          routerName: textOf(pick(intent, 'routerName', 'RouterName')),
          intentId: pick(intent, 'intentId', 'IntentId'),
          intentName: textOf(pick(intent, 'intentName', 'IntentName')),
          intentPath: pick(intent, 'intentPath', 'IntentPath') || [],
          chainLevels: pick(intent, 'chainLevels', 'ChainLevels') || [],
          rawLevel: textOf(pick(intent, 'rawLevel', 'RawLevel')),
          effectiveLevel: textOf(pick(intent, 'effectiveLevel', 'EffectiveLevel')),
        })),
        countByCode: countByCode && typeof countByCode === 'object' ? countByCode : {},
        scannedFlows: Number(pick(data, 'scannedFlows', 'ScannedFlows')) || 0,
        scannedNodes: Number(pick(data, 'scannedNodes', 'ScannedNodes')) || 0,
        warnings: Array.isArray(warnings) ? warnings : [],
      };
      this.usageError = '';
    },
    /**
     * ปรับร่างให้เข้ากับข้อมูลที่โหลดใหม่: ลำดับร่างคงไว้ถ้ายังมีสมาชิกชุดเดิมและแก้ได้ (เช่น หลังแก้ชื่อ) ไม่งั้นกลับเป็นที่บันทึก
     */
    syncDrafts() {
      const saved = this.savedCodes;
      if (!(this.ladderWritable && sameMembers(this.draftCodes, saved))) {
        this.draftCodes = [...saved];
      }
    },
    /** หลังเขียนสำเร็จทุกครั้ง: แจ้งผล → โหลดหน้าใหม่ + ให้ dropdown ทั้งแอปเห็น ladder ใหม่ */
    async afterLadderWrite(message) {
      this.showSnackbar(message, 'success');
      await Promise.all([this.loadLadderSections(), refreshAccessLevels()]);
    },

    // A — ลำดับ (ร่าง) ▲▼
    moveLevel(index, direction) {
      const codes = [...this.draftCodes];
      const target = index + direction;
      // T0 (แถวแรก) และ T3 (แถวท้าย) ล็อก — ย้ายได้เฉพาะช่วงกลาง
      if (index < 1 || index > codes.length - 2 || target < 1 || target > codes.length - 2) return;
      const from = this.levelByCode[codes[index]];
      const to = this.levelByCode[codes[target]];
      if ((from && from.isSystem) || (to && to.isSystem)) return;
      [codes[index], codes[target]] = [codes[target], codes[index]];
      this.draftCodes = codes;
    },
    cancelOrder() {
      this.draftCodes = [...this.savedCodes];
    },
    async saveOrder() {
      if (!this.orderDirty || !this.canEditLevel || !this.ladderWritable || this.ladderBusy) return;
      this.ladderSaving = true;
      try {
        const result = await accessLevelApi.reorder([...this.draftCodes]);
        if (this.applyLadder(result)) this.syncDrafts();
        await this.afterLadderWrite(this.$t('accessLevelLadderOrderSaved'));
      } catch (err) {
        this.showSnackbar(errorTextOf(err, this.$t('accessLevelLadderSaveFailed')), 'error');
      } finally {
        this.ladderSaving = false;
      }
    },

    // A — เพิ่ม / แก้ชื่อ (dialog ยิงทันที)
    openAddLevel() {
      if (!this.canAddLevel || !this.ladderWritable || this.orderDirty) return;
      this.levelDialog = { show: true, mode: 'add', level: null, error: '' };
    },
    openEditLevel(level) {
      if (!this.canEditLevel || !this.ladderWritable || !level) return;
      this.levelDialog = { show: true, mode: 'edit', level, error: '' };
    },
    /** payload จาก `AccessLevelTierDialog` (ตรวจฟอร์มแล้ว) `{ code, nameTh, nameEn, description, insertAfterCode }` */
    async submitLevelDialog(payload) {
      if (this.ladderSaving || !payload) return;
      const dialog = this.levelDialog;
      const isAdd = dialog.mode === 'add';
      if (isAdd ? !this.canAddLevel : !this.canEditLevel || !dialog.level) return;
      const body = { nameTh: payload.nameTh, nameEn: payload.nameEn, description: payload.description };
      const code = isAdd ? payload.code : dialog.level.code;
      this.ladderSaving = true;
      this.levelDialog.error = '';
      try {
        if (isAdd) {
          await accessLevelApi.create({ code, ...body, insertAfterCode: payload.insertAfterCode || null });
        } else {
          await accessLevelApi.update(dialog.level.id, body);
        }
        this.levelDialog.show = false;
        await this.afterLadderWrite(
          this.$t(isAdd ? 'accessLevelLadderCreated' : 'accessLevelLadderUpdated', { code })
        );
      } catch (err) {
        this.levelDialog.error = errorTextOf(err, this.$t('accessLevelLadderSaveFailed'));
      } finally {
        this.ladderSaving = false;
      }
    },
    openUsage(level) {
      this.usageDialog = { show: true, code: upperText(level && level.code) };
    },

    // A — ลบ (ยืนยัน → DELETE; 409 ⇒ แสดง flow/router/intent ที่ยังใช้อยู่)
    confirmDeleteLevel(level) {
      if (!this.canDeleteLevel || !this.ladderWritable || this.orderDirty || !level || level.isSystem) return;
      if (this.requiredCodes.includes(upperText(level.code))) return;
      this.deleteDialog = { show: true, level, conflict: null, error: '' };
    },
    async deleteLevel() {
      const level = this.deleteDialog.level;
      if (!level || this.ladderSaving || !this.canDeleteLevel) return;
      this.ladderSaving = true;
      this.deleteDialog.error = '';
      try {
        await accessLevelApi.remove(level.id);
        this.deleteDialog.show = false;
        await this.afterLadderWrite(this.$t('accessLevelLadderDeleted', { code: level.code }));
      } catch (err) {
        if (err && err.httpStatus === 409) {
          const data = err.data || {};
          const intents = pick(data, 'intents', 'Intents');
          this.deleteDialog.conflict = { message: textOf(err.message), intents: Array.isArray(intents) ? intents : [] };
        } else {
          this.deleteDialog.error = errorTextOf(err, this.$t('accessLevelLadderSaveFailed'));
        }
      } finally {
        this.ladderSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.btn-layout {
  padding: 14px 24px;
  min-width: 160px;
  height: 56px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
}

.description {
  white-space: normal;
}

.dialog-scroll {
  max-height: 65vh;
  overflow-y: auto;
}
</style>
